package fr.frux.core.designsystem.error

import androidx.compose.runtime.Composable
import fr.frux.core.common.error.AppError
import fr.frux.core.common.error.ConflictReason
import fr.frux.core.common.error.InvalidReason
import fr.frux.core.designsystem.resources.Res
import fr.frux.core.designsystem.resources.ds_error_account_suspended
import fr.frux.core.designsystem.resources.ds_error_already_reported
import fr.frux.core.designsystem.resources.ds_error_attach_forbidden
import fr.frux.core.designsystem.resources.ds_error_attach_too_far
import fr.frux.core.designsystem.resources.ds_error_conflict
import fr.frux.core.designsystem.resources.ds_error_invalid
import fr.frux.core.designsystem.resources.ds_error_move_too_far
import fr.frux.core.designsystem.resources.ds_error_network
import fr.frux.core.designsystem.resources.ds_error_not_found
import fr.frux.core.designsystem.resources.ds_error_not_site_owner
import fr.frux.core.designsystem.resources.ds_error_planting_exists
import fr.frux.core.designsystem.resources.ds_error_premium_required
import fr.frux.core.designsystem.resources.ds_error_quota_exhausted
import fr.frux.core.designsystem.resources.ds_error_rate_limited
import fr.frux.core.designsystem.resources.ds_error_rate_limited_minutes
import fr.frux.core.designsystem.resources.ds_error_rate_limited_seconds
import fr.frux.core.designsystem.resources.ds_error_self_report
import fr.frux.core.designsystem.resources.ds_error_server
import fr.frux.core.designsystem.resources.ds_error_site_private
import fr.frux.core.designsystem.resources.ds_error_tree_already_attached
import fr.frux.core.designsystem.resources.ds_error_unauthorized
import fr.frux.core.designsystem.resources.ds_error_unexpected
import org.jetbrains.compose.resources.stringResource

/**
 * Libellé par défaut d'une catégorie d'échec (#176). Le mapping vit ici, en couche UI, et non sur
 * le type : `core-common` est aussi compilé par le serveur, où une ressource Compose n'existe pas
 * et où un libellé d'écran n'a aucun sens. L'état d'un ViewModel porte un [AppError], jamais une
 * chaîne — c'est l'écran qui le traduit, en composition.
 *
 * Un appelant dont le contexte précise le sens (un 401 sur le formulaire de connexion, c'est un
 * code faux, pas une session morte) substitue son propre type avant l'affichage — cf.
 * `AuthError.userMessage()`.
 */
@Composable
fun AppError.userMessage(): String =
    when (this) {
        is AppError.Network -> {
            stringResource(Res.string.ds_error_network)
        }

        is AppError.Unauthorized -> {
            stringResource(Res.string.ds_error_unauthorized)
        }

        is AppError.NotFound -> {
            stringResource(Res.string.ds_error_not_found)
        }

        is AppError.Conflict -> {
            // Message précis fourni par l'appelant (règle métier serveur, ex. #224) : il reste une
            // chaîne, il n'y a pas de catégorie à traduire derrière.
            userMessageOverride ?: conflictMessage(reason)
        }

        is AppError.Invalid -> {
            invalidMessage(reason)
        }

        is AppError.RateLimited -> {
            rateLimitedMessage()
        }

        is AppError.Server -> {
            stringResource(Res.string.ds_error_server)
        }

        is AppError.Unexpected -> {
            stringResource(Res.string.ds_error_unexpected)
        }
    }

@Composable
private fun conflictMessage(reason: ConflictReason?): String =
    when (reason) {
        ConflictReason.TREE_ALREADY_ATTACHED -> stringResource(Res.string.ds_error_tree_already_attached)
        ConflictReason.ATTACH_TOO_FAR -> stringResource(Res.string.ds_error_attach_too_far)
        ConflictReason.PLANTING_EXISTS -> stringResource(Res.string.ds_error_planting_exists)
        ConflictReason.ALREADY_REPORTED -> stringResource(Res.string.ds_error_already_reported)
        null -> stringResource(Res.string.ds_error_conflict)
    }

@Composable
private fun invalidMessage(reason: InvalidReason?): String =
    when (reason) {
        InvalidReason.ACCOUNT_SUSPENDED -> stringResource(Res.string.ds_error_account_suspended)
        InvalidReason.PREMIUM_REQUIRED -> stringResource(Res.string.ds_error_premium_required)
        InvalidReason.MOVE_TOO_FAR -> stringResource(Res.string.ds_error_move_too_far)
        InvalidReason.ATTACH_FORBIDDEN -> stringResource(Res.string.ds_error_attach_forbidden)
        InvalidReason.SELF_REPORT -> stringResource(Res.string.ds_error_self_report)
        InvalidReason.SITE_PRIVATE -> stringResource(Res.string.ds_error_site_private)
        InvalidReason.NOT_SITE_OWNER -> stringResource(Res.string.ds_error_not_site_owner)
        null -> stringResource(Res.string.ds_error_invalid)
    }

@Composable
private fun AppError.RateLimited.rateLimitedMessage(): String {
    // Lié localement : le smart cast est impossible sur une propriété publique d'un autre module.
    val seconds = retryAfterSeconds
    return when {
        quotaExhausted -> {
            stringResource(Res.string.ds_error_quota_exhausted)
        }

        seconds == null -> {
            stringResource(Res.string.ds_error_rate_limited)
        }

        seconds <= SECONDS_PER_MINUTE -> {
            // `%1$d` est formaté par la plateforme : un Long y passe sur Android mais pas partout
            // (implémentation propre de CMP hors Android), d'où le passage explicite en Int.
            stringResource(Res.string.ds_error_rate_limited_seconds, seconds.coerceAtLeast(1).toInt())
        }

        else -> {
            stringResource(
                Res.string.ds_error_rate_limited_minutes,
                ((seconds + SECONDS_PER_MINUTE - 1) / SECONDS_PER_MINUTE).toInt(),
            )
        }
    }
}

private const val SECONDS_PER_MINUTE = 60L
